import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Clock, FileText, Package, Tag, AlertTriangle,
  Camera, Image as ImageIcon, Shield, XCircle, History, Eye,
  MapPin, Wrench, User, Phone, Calendar, Film, Download, Building2, Pen
} from 'lucide-react';

const STATUS_CONFIG = {
  open: { label: 'Deschis', color: 'bg-blue-500 text-white', icon: Clock },
  accepted: { label: 'Acceptat', color: 'bg-amber-500 text-white', icon: Clock },
  in_progress: { label: 'In Lucru', color: 'bg-purple-500 text-white', icon: Clock },
  waiting_parts: { label: 'Asteapta Piese', color: 'bg-orange-500 text-white', icon: Clock },
  monitoring: { label: 'Sub Monitorizare', color: 'bg-yellow-500 text-slate-900', icon: Eye },
  resolved: { label: 'Rezolvat', color: 'bg-emerald-500 text-white', icon: Clock },
  closed: { label: 'Inchis', color: 'bg-slate-500 text-white', icon: Shield },
  rejected: { label: 'Respins', color: 'bg-red-500 text-white', icon: XCircle },
  transformed: { label: 'Transformat', color: 'bg-cyan-500 text-white', icon: Clock }
};

const PRIORITY_CONFIG = {
  critical: { label: 'Critic', color: 'bg-red-600 text-white' },
  urgent: { label: 'Urgent', color: 'bg-orange-500 text-white' },
  high: { label: 'Ridicat', color: 'bg-amber-500 text-white' },
  medium: { label: 'Mediu', color: 'bg-yellow-400 text-slate-900' },
  low: { label: 'Scazut', color: 'bg-green-500 text-white' }
};

const SLA_CONFIG = {
  ok: { label: 'In termen', color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  at_risk: { label: 'Risc', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  breached: { label: 'Depasit', color: 'text-red-600', bg: 'bg-red-50 border-red-200' }
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const PortalTicketDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTicket = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/tickets/${id}`);
      setTicket(response.data);
    } catch {
      toast.error('Tichetul nu a fost gasit');
      navigate('/portal/tickets');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!ticket) return null;

  const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
  const priorityCfg = PRIORITY_CONFIG[ticket.priority_assigned || ticket.priority_requested] || PRIORITY_CONFIG.medium;
  const slaCfg = SLA_CONFIG[ticket.sla_status] || SLA_CONFIG.ok;
  const StatusIcon = statusCfg.icon;

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_BASE}${url}`;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 pb-20 lg:pb-6 space-y-4" data-testid="portal-ticket-detail">
      {/* Read-only Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3" data-testid="readonly-banner">
        <Eye className="w-5 h-5 text-amber-500 flex-shrink-0" />
        <p className="text-sm text-amber-700">Mod vizualizare - informatiile tichetului sunt disponibile doar pentru consultare</p>
      </div>

      {/* Back + Header */}
      <div className="flex items-start gap-2 sm:gap-3">
        <Button variant="ghost" size="icon" className="shrink-0 mt-1" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 break-words" data-testid="ticket-title">{ticket.title}</h1>
            <Badge className={statusCfg.color} data-testid="ticket-status-badge">
              <StatusIcon className="w-3.5 h-3.5 mr-1" />{statusCfg.label}
            </Badge>
            {ticket.status === 'waiting_parts' && ticket.parts_eta_days && (
              <Badge className="bg-orange-100 text-orange-800 border border-orange-300" data-testid="portal-ticket-eta-badge">
                ETA: {ticket.parts_eta_days}z
                {ticket.parts_eta_date && (
                  <span className="ml-1 text-orange-600"> · {new Date(ticket.parts_eta_date).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' })}</span>
                )}
              </Badge>
            )}
            <Badge className={priorityCfg.color} data-testid="ticket-priority-badge">{priorityCfg.label}</Badge>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Creat de <span className="font-medium">{ticket.created_by_name || 'N/A'}</span> la {new Date(ticket.created_at).toLocaleString('ro-RO')}
            {ticket.reporter_name && <span className="ml-2">| Raportat de <span className="font-medium">{ticket.reporter_name}</span></span>}
          </p>
        </div>
      </div>

      {/* SLA Banner */}
      {ticket.sla_deadline && ticket.status !== 'closed' && (
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${slaCfg.bg}`} data-testid="sla-banner">
          <AlertTriangle className={`w-5 h-5 ${slaCfg.color}`} />
          <div>
            <span className={`font-semibold text-sm ${slaCfg.color}`}>SLA: {slaCfg.label}</span>
            <span className="text-sm text-slate-600 ml-2">Termen: {new Date(ticket.sla_deadline).toLocaleString('ro-RO')}</span>
          </div>
        </div>
      )}

      {/* Parts ETA Banner (waiting_parts) - highly visible for section chiefs */}
      {ticket.status === 'waiting_parts' && ticket.parts_eta_days && (() => {
        const etaDate = ticket.parts_eta_date ? new Date(ticket.parts_eta_date) : null;
        let remaining = null;
        if (etaDate) {
          remaining = Math.ceil((etaDate - new Date()) / 86400000);
        }
        const overdue = remaining !== null && remaining < 0;
        const soon = remaining !== null && remaining >= 0 && remaining <= 3;
        return (
          <div
            className={`flex items-center gap-3 sm:gap-4 p-4 rounded-lg border-2 ${overdue ? 'bg-red-50 border-red-300' : soon ? 'bg-amber-50 border-amber-300' : 'bg-orange-50 border-orange-300'}`}
            data-testid="portal-parts-eta-banner"
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${overdue ? 'bg-red-500' : soon ? 'bg-amber-500' : 'bg-orange-500'}`}>
              <Package className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`font-bold text-sm sm:text-base ${overdue ? 'text-red-800' : soon ? 'text-amber-800' : 'text-orange-800'}`}>
                Asteapta Piese - Termen estimativ: <span className="text-lg sm:text-xl font-extrabold">{ticket.parts_eta_days} zile</span>
                {etaDate && <span className="font-semibold"> · {etaDate.toLocaleDateString('ro-RO', { day: '2-digit', month: 'long', year: 'numeric' })}</span>}
              </p>
              {remaining !== null && (
                <p className={`text-xs sm:text-sm mt-0.5 font-medium ${overdue ? 'text-red-700' : soon ? 'text-amber-700' : 'text-orange-700'}`}>
                  {overdue ? `Depasit cu ${Math.abs(remaining)} zile - contactati furnizorul` : remaining === 0 ? 'Piesele ar trebui sa ajunga astazi' : `Mai sunt ${remaining} zile pana la sosirea pieselor`}
                </p>
              )}
              {ticket.parts_eta_set_by && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Setat de <span className="font-medium text-slate-700">{ticket.parts_eta_set_by}</span>
                  {ticket.parts_eta_set_at && <> pe {new Date(ticket.parts_eta_set_at).toLocaleDateString('ro-RO')}</>}
                </p>
              )}
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: Main Info */}
        <div className="lg:col-span-2 space-y-4">
          {/* Description */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-500" /> Descriere Problema
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="ticket-description">
                {ticket.description || 'Fara descriere'}
              </p>
              {ticket.symptom_name && (
                <div className="mt-4 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-slate-500">Simptom:</span>
                  <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">{ticket.symptom_name}</Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Images */}
          {ticket.images?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-slate-500" /> Imagini Atasate ({ticket.images.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {ticket.images.map((img, index) => (
                    <img
                      key={index}
                      src={resolveUrl(img)}
                      alt={`Imagine ${index + 1}`}
                      className="w-28 h-28 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => window.open(resolveUrl(img), '_blank')}
                      data-testid={`ticket-image-${index}`}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Attachments (videos, documents) */}
          {ticket.attachments?.filter(a => a.type !== 'image').length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" /> Documente & Video
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {ticket.attachments.filter(a => a.type !== 'image').map((att, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border bg-slate-50" data-testid={`attachment-${idx}`}>
                      {att.type === 'video' ? (
                        <Film className="w-5 h-5 text-purple-500 flex-shrink-0" />
                      ) : (
                        <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{att.name}</p>
                        <p className="text-xs text-slate-400">{att.type === 'video' ? 'Video' : 'Document'}</p>
                      </div>
                      <a
                        href={resolveUrl(att.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        <Download className="w-4 h-4 text-slate-500" />
                      </a>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Consumed Materials (read-only) */}
          {ticket.consumed_items?.length > 0 && (
            <Card data-testid="consumed-materials-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="w-4 h-4 text-blue-500" /> Materiale Consumate ({ticket.consumed_items.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {ticket.consumed_items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
                      <div>
                        <p className="font-medium text-sm">{item.article_name}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-xs text-slate-500">{item.article_code}</p>
                          {item.supplier_name && <Badge variant="outline" className="text-xs py-0">{item.supplier_name}</Badge>}
                          {item.lot_number && <span className="text-xs text-slate-400">Lot: {item.lot_number}</span>}
                        </div>
                      </div>
                      <span className="font-semibold text-sm">{item.quantity} {item.unit}</span>
                    </div>
                  ))}
                  {ticket.consumed_items.some(i => i.unit_price > 0) && (
                    <div className="flex justify-between items-center p-2 bg-blue-50 rounded-lg border border-blue-200">
                      <span className="text-sm font-medium text-blue-800">Cost total piese:</span>
                      <span className="text-sm font-bold text-blue-900 font-mono">
                        {ticket.consumed_items.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_price || 0), 0).toFixed(2)} RON
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Signatures */}
          {ticket.status === 'closed' && (ticket.technician_signature || ticket.operator_signature) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Pen className="w-4 h-4 text-slate-500" /> Semnaturi Inchidere
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {ticket.technician_signature && (
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-2">Semnatura Tehnician</p>
                      <div className="border rounded-lg p-2 bg-white">
                        <img src={ticket.technician_signature} alt="Semnatura tehnician" className="max-h-24 mx-auto" />
                      </div>
                      {ticket.closed_by_name && <p className="text-xs text-slate-500 mt-1">Inchis de: {ticket.closed_by_name}</p>}
                    </div>
                  )}
                  {ticket.operator_signature && !ticket.operator_signature_skipped && (
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-2">Semnatura Operator</p>
                      <div className="border rounded-lg p-2 bg-white">
                        <img src={ticket.operator_signature} alt="Semnatura operator" className="max-h-24 mx-auto" />
                      </div>
                    </div>
                  )}
                  {ticket.operator_signature_skipped && (
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-2">Semnatura Operator</p>
                      <div className="border rounded-lg p-3 bg-amber-50 text-center">
                        <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                        <p className="text-sm text-amber-700">
                          {ticket.operator_skip_reason === 'not_present' ? 'Operator absent' : 'Operator a refuzat semnarea'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Closure Images */}
          {ticket.status === 'closed' && ticket.closure_images?.length > 0 && (
            <Card data-testid="closure-images-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Camera className="w-4 h-4 text-green-500" /> Dovada Fotografica Inchidere ({ticket.closure_images.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {ticket.closure_images.map((img, index) => (
                    <img
                      key={index}
                      src={resolveUrl(img)}
                      alt={`Dovada ${index + 1}`}
                      className="w-28 h-28 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => window.open(resolveUrl(img), '_blank')}
                      data-testid={`closure-image-${index}`}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revision History */}
          {ticket.revision_history?.length > 0 && (
            <Card data-testid="revision-history-card" className="border-amber-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="w-4 h-4 text-amber-500" /> Istoric Revizuiri ({ticket.revision_count || ticket.revision_history.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[...ticket.revision_history].reverse().map((rev, idx) => (
                    <div key={rev.id || idx} className="p-3 rounded-lg border bg-amber-50/50 border-amber-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-amber-800">Revizuire #{ticket.revision_history.length - idx}</span>
                        <span className="text-xs text-slate-500">{new Date(rev.revised_at).toLocaleString('ro-RO')}</span>
                      </div>
                      <p className="text-xs text-slate-600 mb-1">De: <span className="font-medium">{rev.revised_by_name}</span></p>
                      <p className="text-sm text-amber-900"><strong>Motiv:</strong> {rev.reason}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {ticket.notes && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Note</CardTitle></CardHeader>
              <CardContent>
                <div className="bg-slate-50 p-4 rounded-lg whitespace-pre-wrap text-sm text-slate-700">{ticket.notes}</div>
              </CardContent>
            </Card>
          )}

          {/* Rejection reason */}
          {ticket.rejection_reason && (
            <Card className="border-red-200">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-700 text-sm">Motiv Respingere</p>
                    <p className="text-red-600 mt-1">{ticket.rejection_reason}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Status Timeline */}
          {ticket.status_history?.length > 0 && (
            <Card data-testid="status-timeline-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-500" /> Istoric Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[...ticket.status_history].reverse().map((entry, idx) => {
                    const sCfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.open;
                    return (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="mt-1 w-2.5 h-2.5 rounded-full bg-blue-400 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`text-[10px] px-2 py-0.5 ${sCfg.color}`}>{sCfg.label}</Badge>
                            <span className="text-xs text-slate-500">
                              {new Date(entry.changed_at).toLocaleString('ro-RO')}
                            </span>
                          </div>
                          {entry.changed_by_name && (
                            <p className="text-xs text-slate-500 mt-0.5">De: {entry.changed_by_name}</p>
                          )}
                          {entry.notes && (
                            <p className="text-xs text-slate-600 mt-0.5 italic">{entry.notes}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* RIGHT: Info Sidebar */}
        <div className="space-y-4">
          {/* Ticket Info Card */}
          <Card data-testid="ticket-info-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Detalii Tichet</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoRow icon={Building2} label="Fabrica" value={ticket.plant_name} />
              <InfoRow icon={MapPin} label="Locatie" value={ticket.location_name} />
              <InfoRow icon={Wrench} label="Echipament" value={ticket.equipment_name} />
              {ticket.equipment_code && <InfoRow icon={Tag} label="Cod Echipament" value={ticket.equipment_code} />}
              <div className="border-t pt-3 mt-3" />
              <InfoRow icon={User} label="Creat de" value={ticket.created_by_name} />
              {ticket.reporter_name && <InfoRow icon={User} label="Raportat de" value={ticket.reporter_name} />}
              {ticket.reporter_phone && <InfoRow icon={Phone} label="Telefon" value={ticket.reporter_phone} />}
              <InfoRow icon={Calendar} label="Data creare" value={new Date(ticket.created_at).toLocaleString('ro-RO')} />
              {ticket.closed_at && <InfoRow icon={Calendar} label="Data inchidere" value={new Date(ticket.closed_at).toLocaleString('ro-RO')} />}
              {ticket.assigned_to_name && (
                <>
                  <div className="border-t pt-3 mt-3" />
                  <InfoRow icon={User} label="Asignat la" value={ticket.assigned_to_name} />
                  {ticket.assigned_to_names?.length > 1 && (
                    <div className="pl-7">
                      {ticket.assigned_to_names.map((name, i) => (
                        <p key={i} className="text-xs text-slate-600">{name}</p>
                      ))}
                    </div>
                  )}
                </>
              )}
              {ticket.accepted_by_name && <InfoRow icon={User} label="Acceptat de" value={ticket.accepted_by_name} />}
              {ticket.closed_by_name && <InfoRow icon={User} label="Inchis de" value={ticket.closed_by_name} />}
            </CardContent>
          </Card>

          {/* Priority & SLA */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Prioritate & SLA</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Prioritate solicitata</span>
                <Badge className={`text-[10px] ${(PRIORITY_CONFIG[ticket.priority_requested] || PRIORITY_CONFIG.medium).color}`}>
                  {(PRIORITY_CONFIG[ticket.priority_requested] || PRIORITY_CONFIG.medium).label}
                </Badge>
              </div>
              {ticket.priority_assigned && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Prioritate asignata</span>
                  <Badge className={`text-[10px] ${(PRIORITY_CONFIG[ticket.priority_assigned] || PRIORITY_CONFIG.medium).color}`}>
                    {(PRIORITY_CONFIG[ticket.priority_assigned] || PRIORITY_CONFIG.medium).label}
                  </Badge>
                </div>
              )}
              {ticket.sla_deadline && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">SLA</span>
                  <span className={`text-xs font-semibold ${slaCfg.color}`}>{slaCfg.label}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-[11px] text-slate-400">{label}</p>
        <p className="text-sm text-slate-700">{value}</p>
      </div>
    </div>
  );
};

export default PortalTicketDetailPage;
